import { View } from "@/components/ui";
import { WhatsappSupportBanner } from "@/components/shared/whatsapp-support-banner";
import { onChatSales } from "@/lib/chat-sales";
import { BookingOrderCard } from "@/components/shared/booking/order-card";
import { BookingNavigationBottom } from "@/components/shared/booking/order-card/BookingNavigationBottom";
import { useAuthState } from "@/store/auth";
import { useClientVerifyPhoneNumberStore } from "@/store/auth/clientVerifyPhoneNumber";
import { AuthModals, LoginType } from "@/types/users";
import { useMemo } from "react";
import type { ServiceName } from "@/types/service";
import { useClientCreateReservationStore } from "@/store/auth/clientCreateReservation";
import { addMinutes } from "date-fns";
import { useBookingState } from "@/store/booking/useBookingState";
import {
  PackageDepartmentEnum,
  type ReservationDetailFragmentFragment,
  type ScheduleJobFrequencyEnum,
} from "@/__generated__/graphql";
import type { PackageRecurrenceEnum } from "@/__generated__/graphql";
import { useBookingRoute } from "@/components/hooks/use-booking-route";
import { getFrequencyOfJobFromRecurrence } from "@/lib/booking-lib";
import {
  sendAnalyticData,
  transformBookingDataToAddToCart,
} from "@/lib/monitoring/analytics";
import { useAuth } from "@/components/shared/auth-provider";
import { useBookingOrder } from "@/components/hooks/use-booking-order";
import { useUnfinishedBookingState } from "@/store/booking/useUnfinishedBookingState";
import { generateBookingChatSalesMessage } from "@/lib/chat-sales";

export const SelectSlotNavigation = (props: { name: ServiceName }) => {
  const { forward } = useBookingRoute();

  const {
    data: { userInfo },
    setPhone,
    showModal,
    setType,
  } = useAuthState();

  const { isGuest } = useAuth();

  const {
    data: {
      packageDetailsFilter,
      selectedSlot,
      selectedPackageIds,
      packageDetails,
      totalCleaner,
      department,
    },
    setBookingState,
  } = useBookingState();
  const {
    loading: clientVerifyPhoneNumberLoading,
    fetch: clientVerifyPhoneNumber,
  } = useClientVerifyPhoneNumberStore();
  const {
    fetch: clientCreateReservation,
    loading: clientCreateReservationLoading,
  } = useClientCreateReservationStore();

  const disabled = useMemo(() => !selectedSlot, [selectedSlot]);

  const { grandTotal } = useBookingOrder();

  const { setUnfinishedBooking } = useUnfinishedBookingState();

  const items =
    packageDetails?.filter((pkg) => selectedPackageIds?.includes(pkg.id)) ?? [];

  const onContinue = async () => {
    if (!selectedSlot) return;

    sendAnalyticData({
      name: "find_availability",
      values: {
        currency: "SGD",
        value: grandTotal,
        items: items.map((item) => ({
          item_id: item.code,
          item_category: item.serviceType,
          price: item.unitValue,
          quantity: item.units * (item.repeatEveryTimes || 1),
        })),
      },
    });

    if (isGuest) {
      showModal(AuthModals.LOGIN);
      return;
    }

    if (!userInfo?.user?.phoneNumberVerifiedAt) {
      const phone = userInfo.user.phoneNumber as string;
      setPhone(phone);
      const res = await clientVerifyPhoneNumber({
        requestPayload: {
          input: {
            phone,
            enableWhatsappOtp: true,
          },
        },
      });

      if (res.error && !res.error?.includes("Last pin code is still active")) {
        return;
      }

      setType(LoginType.Confirm);
      showModal(AuthModals.OTP);
      return;
    }

    const reservationPromises: Promise<
      ReservationDetailFragmentFragment[] | null
    >[] = [];

    const reservationInput = {
      startTime: selectedSlot.startTime,
      endTime: selectedSlot.endTime,
      frequencyOfJob: getFrequencyOfJobFromRecurrence(
        packageDetailsFilter.repeatEvery?.[0] as PackageRecurrenceEnum,
      ) as ScheduleJobFrequencyEnum,
      lineItemIds: selectedPackageIds,
      serviceDate: selectedSlot.date,
      slotId: selectedSlot.slotId,
    };

    if (department === PackageDepartmentEnum.DeepCleaning) {
      for (let i = 0; i < totalCleaner; i++) {
        reservationPromises.push(
          clientCreateReservation({
            requestPayload: { input: reservationInput },
          }).then((res) => {
            if (res.error || !res.data) return null;
            return res.data as ReservationDetailFragmentFragment[];
          }),
        );
      }
    } else {
      reservationPromises.push(
        clientCreateReservation({
          requestPayload: { input: reservationInput },
        }).then((res) => {
          if (res.error || !res.data) return null;
          return res.data as ReservationDetailFragmentFragment[];
        }),
      );
    }

    const responses = await Promise.all(reservationPromises);
    const successfulReservations = responses
      .flat()
      .filter(Boolean) as ReservationDetailFragmentFragment[];

    if (successfulReservations.length === 0) return;

    sendAnalyticData({
      name: "add_to_cart",
      values: transformBookingDataToAddToCart(items),
    });

    const timeStamp = addMinutes(new Date(), 15).getTime();
    setUnfinishedBooking({
      serviceName: props.name,
      bookingState: {
        ...useBookingState.getState().data,
        selectedSlot: null,
        timeSlots: null,
      },
    });
    setBookingState({
      reservationTimeStamp: timeStamp,
      reservations: successfulReservations,
    });
    forward();
  };

  return (
    <View>
      {/* large screen */}
      <View className="hidden flex-col gap-4 md:flex">
        <BookingOrderCard
          onContinue={onContinue}
          disabled={disabled}
          loading={
            clientVerifyPhoneNumberLoading || clientCreateReservationLoading
          }
        />
        <WhatsappSupportBanner
          onClick={() =>
            onChatSales(true, generateBookingChatSalesMessage(props.name))
          }
        />
      </View>
      {/* narrow screen */}
      <BookingNavigationBottom
        name={props.name}
        onContinue={onContinue}
        disabled={disabled}
        loading={
          clientVerifyPhoneNumberLoading || clientCreateReservationLoading
        }
      />
    </View>
  );
};
